import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { OptimizedImage } from "@/components/OptimizedImage";
import { useLanguage } from "@/context/LanguageContext";
import { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE, NAV_LINKS, telHref } from "@/config/site";
import { WhatsAppButton } from "@/components/ActionButtons";
import { useEnquiry } from "@/context/EnquiryContext";
import { canonicalPath } from "@/lib/canonicalPath";

const Logo = () => {
  const { t, localizePath } = useLanguage();
  return (
  <Link to={localizePath("/")} data-testid="header-logo" className="flex items-center group" aria-label={t("{brand} home", { brand: SITE.brand })}>
    <OptimizedImage
      src="/logo.png"
      sizes="48px"
      loading="eager"
      alt={t("Jitendra Singh Tour Operator Service logo")}
      className="h-11 sm:h-12 w-auto drop-shadow"
      width="512"
      height="512"
    />
    <span className="ml-2.5 leading-tight hidden sm:block">
      <span className="block font-extrabold tracking-tight text-white text-base lg:text-lg">Jitendra Singh</span>
      <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-400">Tour Operator Service</span>
    </span>
  </Link>
  );
};

export const Header = () => {
  const { t, localizePath, basePath } = useLanguage();
  const [open, setOpen] = useState(false);
  const menuButton = useRef(null);
  const { openEnquiry } = useEnquiry();
  const { pathname } = useLocation();
  const currentPath = canonicalPath(basePath(pathname));
  const isCurrent = (path) => currentPath === canonicalPath(path) ||
    (path !== "/" && currentPath.startsWith(canonicalPath(path)));

  return (
    <header onKeyDown={(event) => { if (event.key === "Escape" && open) { setOpen(false); menuButton.current?.focus(); } }} className="sticky top-0 z-50 border-b border-slate-800 bg-[#071426]/95 backdrop-blur-md">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-3">
          <Logo />

          <nav className="hidden 2xl:flex items-center gap-1" aria-label={t("Primary")}>
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={localizePath(canonicalPath(link.path))}
                aria-current={isCurrent(link.path) ? "page" : undefined}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={
                  `relative rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                    isCurrent(link.path) ? "text-amber-400" : "text-slate-200 hover:text-white"
                  }`
                }
              >
                {t(link.label)}
                {link.badge && (
                  <span className="ml-1 rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-300">
                    {t(link.badge)}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2">
          <div className="hidden 2xl:flex items-center gap-2">
            <a
              href={telHref(SITE.phonePrimary.tel)}
              data-testid="header-call-button"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-sky-500/40 bg-sky-600/10 px-3 py-2 text-sm font-semibold text-sky-300 transition-colors hover:bg-sky-600/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              <Phone className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="whitespace-nowrap">{SITE.phonePrimary.display}</span>
            </a>
            <WhatsAppButton
              testId="header-whatsapp-button"
              className="bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700"
            />
          </div>

          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle" ref={menuButton} aria-controls="mobile-navigation"
            className="2xl:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-100 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            aria-label={t(open ? "Close menu" : "Open menu")}
            aria-expanded={open}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          </div>
        </div>
      </div>

      {open && (
        <div id="mobile-navigation" className="2xl:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-slate-800 bg-[#071426]" data-testid="mobile-menu">
          <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1" aria-label={t("Mobile")}>
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={localizePath(canonicalPath(link.path))}
                aria-current={isCurrent(link.path) ? "page" : undefined}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={
                  `rounded-lg px-3 py-3 text-base font-medium ${
                    isCurrent(link.path) ? "bg-slate-800 text-amber-400" : "text-slate-100 hover:bg-slate-800"
                  }`
                }
              >
                {t(link.label)}
                {link.badge && (
                  <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-bold text-amber-300">
                    {t(link.badge)}
                  </span>
                )}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openEnquiry();
              }}
              data-testid="mobile-menu-enquire-button"
              className="mt-2 rounded-lg bg-amber-500 px-3 py-3 text-base font-bold text-slate-950"
            >
              {t("Request a Quote")}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};
